import { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser'
import { authInitialConfig } from './api'
import { AuthInitialConfig } from './models'

let jwtTokenPromise: Promise<AuthenticationResult | null> | null = null
let msalInstancePromise: Promise<PublicClientApplication> | null = null
let cachedAuthConfig: AuthInitialConfig | null = null
const debug: boolean = false

export async function getIdToken(forceRefresh = false): Promise<string> {
  const jwt = await getJwtToken(forceRefresh)
  if (jwt) {
    return jwt.idToken
  }
  return ''
}

export async function getUserNameToken(): Promise<string | undefined> {
  const jwt = await getJwtToken()
  if (jwt) {
    return jwt.account.name
  }
  return
}

export async function getJwtToken(forceRefresh = false): Promise<AuthenticationResult | null> {
  // If forceRefresh is true, clear any existing promise cache to ensure we get a fresh token
  if (forceRefresh && jwtTokenPromise) {
    jwtTokenPromise = null
  }

  if (!jwtTokenPromise) {
    jwtTokenPromise = (async () => {
      try {
        return await internalGetJwtToken(forceRefresh)
      } finally {
        jwtTokenPromise = null
      }
    })()
  }
  return jwtTokenPromise
}

async function getAuthConfig(): Promise<AuthInitialConfig | null> {
  if (cachedAuthConfig) {
    return cachedAuthConfig
  }

  try {
    cachedAuthConfig = await authInitialConfig()
    return cachedAuthConfig
  } catch (error) {
    console.error('Failed to get auth config:', error)
    return null
  }
}

async function internalGetJwtToken(forceRefresh = false): Promise<AuthenticationResult | null> {
  debug && console.log('internalGetJwtToken() - start')

  const settings = await getAuthConfig()
  debug && console.log('internalGetJwtToken() - settings', settings)

  if (!settings || !settings.auth) {
    console.error('internalGetJwtToken() - Missing auth settings')
    throw new Error('Missing auth settings')
  }

  if (!settings.auth.enabled) {
    debug && console.log('internalGetJwtToken() - Auth disabled')
    return null
  }

  const msalInstance = await initializeMsalInstance(settings)

  const redirectResponse = await msalInstance.handleRedirectPromise()
  if (redirectResponse) {
    debug && console.log('Got token from redirect:', redirectResponse)
    return redirectResponse
  }

  const tokenRequest = { scopes: settings.auth.scopes }

  if (msalInstance.getAllAccounts().length === 0) {
    debug && console.log('No accounts, redirecting to login...')
    await msalInstance.acquireTokenRedirect(tokenRequest)
    return null
  } else {
    // If forceRefresh is true, pass it to refreshToken so it forces a refresh
    return refreshToken(msalInstance, tokenRequest, forceRefresh)
  }
}

export async function signOut() {
  const settings = await getAuthConfig()
  if (!settings) return

  const msalInstance = await initializeMsalInstance(settings)

  msalInstancePromise = null
  cachedAuthConfig = null

  await msalInstance.logoutRedirect()
}

async function refreshToken(
  msalInstance: PublicClientApplication,
  tokenRequest: any,
  forceRefresh = false,
): Promise<AuthenticationResult | null> {
  debug && console.log('User already logged in')
  if (msalInstance.getActiveAccount() === null) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0])
  }

  try {
    // If forceRefresh is true, use it in the token request to force a refresh
    const request = forceRefresh ? { ...tokenRequest, forceRefresh: true } : tokenRequest
    const token = await msalInstance.acquireTokenSilent(request)
    debug && console.log('Silent token:', token)

    // Only check expiration if we didn't force refresh
    if (!forceRefresh && token.expiresOn) {
      const tokenExpirationTime = token.expiresOn.getTime()
      const currentTime = new Date().getTime()
      const minutesUntilExpiration = (tokenExpirationTime - currentTime) / (1000 * 60)

      if (minutesUntilExpiration <= 15) {
        return msalInstance.acquireTokenSilent({ ...tokenRequest, forceRefresh: true })
      }
    }

    return token
  } catch (error) {
    console.error('refreshToken() - Silent token acquisition failed', error)
    await msalInstance.clearCache()
    await msalInstance.acquireTokenRedirect(tokenRequest)
    return null
  }
}

async function initializeMsalInstance(settings: AuthInitialConfig): Promise<PublicClientApplication> {
  if (msalInstancePromise) {
    return msalInstancePromise
  }

  msalInstancePromise = (async () => {
    const msalInstance = new PublicClientApplication({
      auth: {
        clientId: settings.auth!.client_id,
        authority: settings.auth!.authority,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: true,
      },
    })

    await msalInstance.initialize()
    debug && console.log('MSAL instance initialized:', msalInstance)
    return msalInstance
  })()

  return msalInstancePromise
}
